<script setup>
import ReportFilterSelector from '../../settings/reports/components/FilterSelector.vue';
import { formatTime } from '@chatwoot/utils';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import { generateFileName } from 'dashboard/helper/downloadHelper';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { computed, onMounted, ref, h } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: 'account',
  },
  getterKey: {
    type: String,
    default: '',
  },
  actionKey: {
    type: String,
    default: '',
  },
  summaryKey: {
    type: String,
    default: '',
  },
  fetchItemsKey: {
    type: String,
    required: true,
  },
});

const store = useStore();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const channelId = ref(null);
const agentId = ref(null);
import { useI18n } from 'vue-i18n';
import SummaryReportLink from '../../settings/reports/components/SummaryReportLink.vue';

const rowItems = useMapGetter([props.getterKey]) || [];
const reportMetrics = useMapGetter(['gmAnalytics/getAgentPerformanceMatrix']) || [];

const getMetrics = id => {
  console.log(`🔍 GmSummaryReports: Getting metrics for agent ID: ${id}`);
  console.log('🔍 GmSummaryReports: reportMetrics.value:', reportMetrics.value);
  console.log('🔍 GmSummaryReports: reportMetrics type:', typeof reportMetrics.value);
  console.log('🔍 GmSummaryReports: reportMetrics is array:', Array.isArray(reportMetrics.value));
  
  if (!reportMetrics.value || !Array.isArray(reportMetrics.value)) {
    console.log('⚠️ GmSummaryReports: reportMetrics is not an array:', reportMetrics.value);
    return {};
  }
  
  const found = reportMetrics.value.find(metrics => metrics.id === Number(id));
  console.log(`🔍 GmSummaryReports: Found metrics for ID ${id}:`, found);
  return found || {};
};
const columnHelper = createColumnHelper();
const { t } = useI18n();

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-n-slate-12',
    },
    cellProps.getValue()
  );

const columns = computed(() => [
  columnHelper.accessor('name', {
    header: t(`GM_ANALYTICS.AGENT_PERFORMANCE_MATRIX.${props.type.toUpperCase()}`),
    width: 200,
    cell: cellProps => h(SummaryReportLink, cellProps),
  }),
  columnHelper.accessor('conversationsCount', {
    header: t('GM_ANALYTICS.AGENT_PERFORMANCE_MATRIX.CONVERSATIONS'),
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgFirstResponseTime', {
    header: 'First Response',
    width: 130,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('avgResolutionTime', {
    header: 'Resolution Time',
    width: 130,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('resolutionsCount', {
    header: 'Resolved',
    width: 100,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('sentimentScore', {
    header: 'Sentiment',
    width: 120,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('customerSatisfaction', {
    header: 'CSAT',
    width: 100,
    cell: defaulSpanRender,
  }),
]);

const renderAvgTime = value => (value ? formatTime(value) : '--');

const renderCount = value => (value ? value.toLocaleString() : '--');

const renderSentiment = value => {
  if (!value) return '--';
  const score = parseFloat(value);
  if (score >= 0.6) return `${(score * 100).toFixed(1)}% 😊`;
  if (score >= 0.4) return `${(score * 100).toFixed(1)}% 😐`;
  return `${(score * 100).toFixed(1)}% 😞`;
};

const renderCSAT = value => {
  if (!value) return '--';
  return `${(value * 100).toFixed(1)}%`;
};

const tableData = computed(() => {
  console.log('🔍 GmSummaryReports: Computing table data...');
  console.log('🔍 GmSummaryReports: rowItems.value:', rowItems.value);
  console.log('🔍 GmSummaryReports: reportMetrics.value:', reportMetrics.value);
  
  return rowItems.value.map(row => {
    const rowMetrics = getMetrics(row.id);
    console.log(`🔍 GmSummaryReports: Agent ${row.id} (${row.name}) metrics:`, rowMetrics);
    
    // Map the backend field names to frontend field names
    const {
      conversations_count,
      avg_first_response_time,
      avg_resolution_time,
      resolved_conversations_count,
      avg_sentiment_score,
      avg_customer_satisfaction,
    } = rowMetrics;
    
    const mappedData = {
      id: row.id,
      // we fallback on title, label for instance does not have a name property
      name: row.name ?? row.title,
      type: props.type,
      conversationsCount: renderCount(conversations_count),
      avgFirstResponseTime: renderAvgTime(avg_first_response_time),
      avgResolutionTime: renderAvgTime(avg_resolution_time),
      resolutionsCount: renderCount(resolved_conversations_count),
      sentimentScore: renderSentiment(avg_sentiment_score),
      customerSatisfaction: renderCSAT(avg_customer_satisfaction),
    };
    
    console.log(`✅ GmSummaryReports: Mapped data for ${row.name}:`, mappedData);
    return mappedData;
  });
});

const fetchAllData = async () => {
  console.log('🔄 GmSummaryReports: Starting to fetch data...');
  console.log('🔍 GmSummaryReports: Props:', props);
  console.log('🔍 GmSummaryReports: Fetch items key:', props.fetchItemsKey);
  console.log('🔍 GmSummaryReports: Action key:', props.actionKey);
  
  try {
    // Fetch agents first
    console.log('👥 GmSummaryReports: Fetching agents...');
    await store.dispatch(props.fetchItemsKey);
    console.log('✅ GmSummaryReports: Agents fetched');
    
    // Then fetch agent performance matrix data
    console.log('📊 GmSummaryReports: Fetching agent performance matrix...');
    console.log('🔍 GmSummaryReports: Action key:', props.actionKey);
    console.log('🔍 GmSummaryReports: Store modules:', Object.keys(store._modules.root._children));
    
    const filterParams = {
      from: from.value,
      to: to.value,
      businessHours: businessHours.value,
      channelId: channelId.value,
      agentId: agentId.value,
    };
    
    console.log('🔧 GmSummaryReports: Dispatching with params:', filterParams);
    
    const performanceData = await store.dispatch(props.actionKey, filterParams);
    console.log('✅ GmSummaryReports: Performance data fetched:', performanceData);
    console.log('🔍 GmSummaryReports: Performance data type:', typeof performanceData);
    console.log('🔍 GmSummaryReports: Performance data is array:', Array.isArray(performanceData));
  } catch (error) {
    console.error('❌ GmSummaryReports: Error fetching data:', error);
  }
};

onMounted(() => fetchAllData());

const onFilterChange = updatedFilter => {
  console.log('🔧 GmSummaryReports: Filter change received:', updatedFilter);
  console.log('🔧 GmSummaryReports: selectedInbox:', updatedFilter.selectedInbox);
  console.log('🔧 GmSummaryReports: selectedAgents:', updatedFilter.selectedAgents);
  
  from.value = updatedFilter.from;
  to.value = updatedFilter.to;
  businessHours.value = updatedFilter.businessHours;
  channelId.value = updatedFilter.selectedInbox ? updatedFilter.selectedInbox.id : null;
  agentId.value = updatedFilter.selectedAgents && updatedFilter.selectedAgents.length > 0 ? updatedFilter.selectedAgents[0].id : null;
  
  console.log('🔧 GmSummaryReports: Set channelId to:', channelId.value);
  console.log('🔧 GmSummaryReports: Set agentId to:', agentId.value);
  
  fetchAllData();
};

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
});

// downloadReports method is not used in this component
// but it is exposed to be used in the parent component
const downloadReports = () => {
  const dispatchMethods = {
    agent: 'downloadAgentReports',
    label: 'downloadLabelReports',
    inbox: 'downloadInboxReports',
    team: 'downloadTeamReports',
  };
  if (dispatchMethods[props.type]) {
    const fileName = generateFileName({
      type: props.type,
      to: to.value,
      businessHours: businessHours.value,
    });
    const params = {
      from: from.value,
      to: to.value,
      fileName,
      businessHours: businessHours.value,
    };
    store.dispatch(dispatchMethods[props.type], params);
  }
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFilterSelector 
    :show-inbox-filter="true"
    :show-agents-filter="true"
    @filter-change="onFilterChange" 
  />
  <div
    class="flex-1 overflow-auto px-2 py-2 mt-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="overflow-x-auto">
      <Table :table="table" class="min-w-full compact-table" />
    </div>
  </div>
</template>

<style scoped>
.compact-table {
  font-size: 0.875rem;
}

.compact-table :deep(.table-cell) {
  padding: 0.5rem 0.75rem;
}

.compact-table :deep(.table-header) {
  padding: 0.75rem 0.75rem;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
